import React, { useCallback, useState } from 'react'

import { useDrop, useDrag } from '@sketch/modules-common'

import { useToast } from '@sketch/toasts'

import { useTransferStorageItems } from '../../storageItems'
import { ErrorHandler } from '@sketch/tracing'
import { MovedToProjectToast } from '../../storageItems/components'
import {
  StorageItemFragment,
  useGetDocumentListSettingsQuery,
} from '@sketch/gql-types'

interface UseNestedProjectDropSharesProps {
  projectIdentifier: string
  workspaceId: string
  disabled?: boolean
}

/**
 * Creates a droppable area for nested projects, accepts dragging both shares
 * or projects.
 */
export const useNestedProjectDropShares = ({
  projectIdentifier,
  workspaceId,
  disabled = false,
}: UseNestedProjectDropSharesProps) => {
  const { showToast } = useToast()
  const [isDroppableAreaEnabled, setIsDroppableAreaEnabled] = useState(false)

  const [transferItemsRaw] = useTransferStorageItems({
    onError: errorMessage => {
      showToast(errorMessage, 'negative')
    },
  })

  const { data } = useGetDocumentListSettingsQuery()

  const transferItem = useCallback(
    ({
      item,
      projectId,
      oldProjectId,
    }: {
      item: Pick<
        StorageItemFragment,
        '__typename' | 'identifier' | 'archivedAt' | 'deletedAt'
      >
      projectId: string
      oldProjectId: string | undefined
    }) => {
      transferItemsRaw({
        onCompleted: data => {
          const { storageItems } = data.transferStorageItems
          const [storageItem] = storageItems

          const newProject =
            storageItem.__typename === 'Share'
              ? storageItem.project
              : storageItem.parentProjects.find(
                  x => x.identifier === storageItem.parentProjectIdentifier
                )

          if (!newProject) {
            ErrorHandler.shouldNeverHappen('newProject is not defined')
            return
          }

          showToast(
            <MovedToProjectToast
              project={newProject}
              type={storageItem.__typename === 'Share' ? 'Document' : 'Folder'}
              workspaceId={workspaceId}
            />
          )
        },
        items: [item],
        location: {
          source: { workspaceId, projectId: oldProjectId },
          destination: { workspaceId, projectId },
        },
      })
    },
    [showToast, transferItemsRaw, workspaceId]
  )

  const dragEvents = useDrag({
    type: 'project',
    identifier: projectIdentifier,
    ...(data?.documentsLayout === 'GRID'
      ? {
          customDragImage: true,
          customDragImageStyles: {
            transform: 'scale(0.8)',
          },
        }
      : {}),
  })

  const dropEvents = useDrop({
    onShareDropped: ({ share }) => {
      if (!share) {
        ErrorHandler.shouldNeverHappen('share is not defined')
        return
      }

      transferItem({
        item: share,
        projectId: projectIdentifier,
        oldProjectId: share?.project?.identifier,
      })
    },
    onProjectDropped: ({ project }) => {
      if (!project) {
        ErrorHandler.shouldNeverHappen('project is not defined')
        return
      }

      transferItem({
        item: project,
        projectId: projectIdentifier,
        oldProjectId: project?.parentProjectIdentifier ?? undefined,
      })
    },
    onProjectDragOver: () => {
      setIsDroppableAreaEnabled(true)
    },
    onShareDragOver: () => {
      setIsDroppableAreaEnabled(true)
    },
    onUnknownDragOver: () => {
      setIsDroppableAreaEnabled(false)
    },
  })

  return {
    draggable: !disabled,
    isDroppableAreaEnabled,
    ...dragEvents,
    ...dropEvents,
  }
}
